import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { GlobalStore } from '../../store';
import { kickUser, toggleHelp, claimUser } from '../../actions/assistantActions';
import { openSendMessageModal, openSendBadLocationModal } from '../../actions/modalActions';
import QueueEntry from '../../models/QueueEntry';
import User from '../../models/User';
import { CheckMark, Clock, Copy, Cross, Envelope, QuestionMark, Star, Square } from '../../viewcomponents/FontAwesome';
import { shortName } from '../../utils/UtilityFunctions';
import { FEATURE_CALL_DIBS } from '../../config';

export default (props: any): JSX.Element => {

  const index: number = props.index;
  const queueEntry: QueueEntry = props.queueEntry;
  const queueName: string = props.queueName;
  const showHelperToAssistants: boolean = props.showHelperToAssistants;
  const onHelpStarted: (() => void) | undefined = props.onHelpStarted;
  const helperNames: string[] = props.helperNames || [];

  const user = useSelector<GlobalStore, User | null>(store => store.user);
  const [mayAdministerQueue, setMayAdministerQueue] = useState(user !== null && (user.isAssistantIn(queueName) || user.isTeacherIn(queueName)));

  useEffect(() => {
    setMayAdministerQueue(user !== null && (user.isAssistantIn(queueName) || user.isTeacherIn(queueName)));
  }, [user, queueName]);

  const [displayAdministrationOptions, setDisplayAdministrationOptions] = useState(false);
  const [copied, setCopied] = useState(false);

  const dispatch = useDispatch();

  function clickRow(): void {
    setDisplayAdministrationOptions(!displayAdministrationOptions);
  }

  function getLocationColor(location: string) {
    location = location.toLowerCase();
    const pattern = /^(blue|blå|red|röd|orange|yellow|gul|green|grön|brown|brun|grey|gray|grå|karmosin|white|vit|magenta|violett|turkos|turquoise|game|play|spel|sport|music|musik|art|konst|food|mat)/g;

    let result: string | null = null;
    const match = location.match(pattern);
    if (match !== null) {
      result = match[0];
    }

    switch (result) {
      case "blue":
        return "blue";
      case "blå":
        return "blue";
      case "red":
        return "red";
      case "röd":
        return "red";
      case "orange":
        return "#FF7F00";
      case "yellow":
        return "yellow";
      case "gul":
        return "yellow";
      case "green":
        return "green";
      case "grön":
        return "green";
      case "brown":
        return "brown";
      case "brun":
        return "brown";
      case "grey":
        return "grey";
      case "gray":
        return "grey";
      case "grå":
        return "grey";
      case "karmosin":
        return "#D91536";
      case "white":
        return "white";
      case "vit":
        return "white";
      case "magenta":
        return "magenta";
      case "violett":
        return "#AC00E6";
      case "turquoise":
        return "turquoise";
      case "turkos":
        return "turquoise";
      case "game":
        return "#E6ADAD";
      case "play":
        return "#E6ADAD";
      case "spel":
        return "#E6ADAD";
      case "sport":
        return "#ADADE6";
      case "music":
        return "#ADE7AD";
      case "musik":
        return "#ADE7AD";
      case "art":
        return "#E8E7AF";
      case "konst":
        return "#E8E7AF";
      case "food":
        return "#E8C9AF";
      case "mat":
        return "#E8C9AF";
      default:
        return "transparent";
    }
  }

  function getRowStyle(): { className?: string; style?: React.CSSProperties } {
    // Actively getting help wins over a stale bad-location flag, so the row
    // turns green as soon as a TA starts helping the student.
    if (queueEntry.gettinghelp) {
      const isMyStudent = user !== null && queueEntry.helperProviderId === user.providerId;
      return {
        className: 'table-success',
        style: isMyStudent ? { borderLeft: '4px solid #155724' } : undefined,
      };
    }

    // >>> call-dibs feature (revertible) >>>
    // Claimed ("dibs") but not yet actively helped: amber. Sits below green so
    // active help always wins, above red so "help is on the way" beats a stale
    // bad-location flag.
    if (FEATURE_CALL_DIBS && queueEntry.claimed) {
      const isMyStudent = user !== null && queueEntry.helperProviderId === user.providerId;
      return {
        className: 'table-warning',
        style: isMyStudent ? { borderLeft: '4px solid #856404' } : undefined,
      };
    }
    // <<< call-dibs feature <<<

    if (queueEntry.badlocation) {
      return { className: 'table-danger' };
    }

    return {};
  }

  function formatBookingTime(isoString: string): string {
    const d = new Date(isoString);
    const hh = d.getHours().toString().padStart(2, '0');
    const mm = d.getMinutes().toString().padStart(2, '0');
    return `booked ${hh}:${mm}`;
  }

  function isBookingRelevant(isoString: string): boolean {
    const now = new Date();
    const booking = new Date(isoString);
    const windowStart = new Date(booking.getTime() - 30 * 60 * 1000);   // 30 min before
    const windowEnd   = new Date(booking.getTime() + 30 * 60 * 1000);   // 30 min after
    return now >= windowStart && now <= windowEnd;
  }

  function checkUrlLocation(locationToCheck: string): [boolean, string | null, string | null] {
    const zoomRegex = /^(https:\/\/)?kth-se\.zoom\.us\/.+/g;
    if (locationToCheck.match(zoomRegex) !== null) {
      return [
        true,
        'Zoom',
        !locationToCheck.startsWith('https://') ? `https://${locationToCheck}` : locationToCheck
      ];
    }

    return [false, null, null];
  }

  const rowStyle = getRowStyle();

  return (
    <>
      <tr
        onClick={mayAdministerQueue ? () => clickRow() : undefined}
        className={`${rowStyle.className || ''}${(mayAdministerQueue ? ' clickable' : '')}`}
        style={rowStyle.style}>
        <th scope="row">{index + 1}</th>
        { user === null
          ? null
          : <td>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.25rem' }}>
                <span>
                  {queueEntry.gettinghelp && <><CheckMark color="#155724" /> </>}
                  {queueEntry.badlocation && !queueEntry.gettinghelp && <><QuestionMark color="#856404" /> </>}
                  {
                    user?.providerId === queueEntry.providerId
                      ? <><Star color="#f0d73a" /> {queueEntry.realname}</>
                      : queueEntry.realname
                  }
                  {
                    queueEntry.bookingTime && isBookingRelevant(queueEntry.bookingTime)
                      && queueEntry.bookingHelp === queueEntry.help && (
                      <span className="ml-2 badge" style={{fontSize: '0.8em', background: '#e67e22', color: '#fff'}}>
                        {formatBookingTime(queueEntry.bookingTime)}
                      </span>
                    )
                  }
                  {
                    queueEntry.gettinghelp && queueEntry.helper && (
                      user.isTeacherIn(queueName) ||
                      (user.isAssistantIn(queueName) && showHelperToAssistants)
                    ) && (
                      <div style={{fontSize: '0.85em', color: '#555', fontStyle: 'italic'}}>
                        {shortName(queueEntry.helper, helperNames)}{queueEntry.helpstarttime && ` ${Math.max(1, Math.round((Date.now() - new Date(queueEntry.helpstarttime).getTime()) / 60_000))} min`}
                      </div>
                    )
                  }
                  {
                    /* >>> call-dibs feature (revertible) >>>
                       Claimed but not yet being helped. Privileged viewers
                       (teachers, and assistants when allowed) see who claimed it;
                       every student just sees a reassuring "help is on the way". */
                    FEATURE_CALL_DIBS && queueEntry.claimed && !queueEntry.gettinghelp && (
                      <div style={{fontSize: '0.85em', color: '#856404', fontStyle: 'italic'}}>
                        {
                          (user.isTeacherIn(queueName) || (user.isAssistantIn(queueName) && showHelperToAssistants)) && queueEntry.helper
                            ? `${shortName(queueEntry.helper, helperNames)} · on my way`
                            : 'help is on the way'
                        }
                      </div>
                    )
                    /* <<< call-dibs feature <<< */
                  }
                </span>
                <span style={{ flexShrink: 0 }}>
                  <Square color={getLocationColor(queueEntry.location)} />
                </span>
              </div>
            </td>
        }
        <td>
        {
          checkUrlLocation(queueEntry.location)[0]
            ? user === null
              ? <span className='text-danger'>Zoom (log in to access)</span>
              : <a
                  href={checkUrlLocation(queueEntry.location)[2] || '#'}
                  target="_blank"
                  rel="noopener noreferrer">
                    {checkUrlLocation(queueEntry.location)[1]}
                </a>
            : queueEntry.location
        }
        <div style={{ fontSize: '0.75em', color: '#888' }}>
          {Math.max(1, Math.round((Date.now() - new Date(queueEntry.starttime).getTime()) / 60_000))} min
        </div>
        </td>
        <td>
          <span className="d-md-none">{queueEntry.help ? 'H' : 'P'}</span>
          <span className="d-none d-md-inline">{queueEntry.help ? 'help' : 'present'}</span>
        </td>
        <td>{queueEntry.comment}</td>
      </tr>
      {
        !displayAdministrationOptions
          ? null
          : <>
              <tr style={{display: 'none'}}></tr>
              <tr>
                <td colSpan={6}>
                  <div className="row my-1">
                    <div title="kick user" aria-label="kick user" className="col-12 col-lg px-3 my-1" onClick={() => dispatch(kickUser(queueName, queueEntry.providerId))}>
                      <div
                        className="text-center red clickable text-white"
                        style={{lineHeight: '2.5em'}}>
                        <Cross />
                      </div>
                    </div>
                    <div
                      title="send message"
                      aria-label="send message"
                      className="col-12 col-lg px-3 my-1"
                      onClick={() => dispatch(openSendMessageModal(queueName, queueEntry.providerId, queueEntry.realname))}>
                      <div
                        className="text-center blue clickable text-white"
                        style={{lineHeight: '2.5em'}}>
                        <Envelope />
                      </div>
                    </div>
                    {
                      queueEntry.gettinghelp
                        ? <div
                            title="stop helping"
                            aria-label="stop helping"
                            className="col-12 col-lg px-3 my-1"
                            onClick={() => dispatch(toggleHelp(queueName, queueEntry.providerId, !queueEntry.gettinghelp))}>
                            <div
                              className="text-center yellow clickable text-white"
                              style={{lineHeight: '2.5em'}}>
                              <CheckMark />
                            </div>
                          </div>
                        : <div
                            title="help"
                            aria-label="help"
                            className="col-12 col-lg px-3 my-1"
                            onClick={() => {
                              dispatch(toggleHelp(queueName, queueEntry.providerId, true));
                              if (onHelpStarted) onHelpStarted();
                            }}>
                            <div
                              className="text-center theme clickable text-white"
                              style={{lineHeight: '2.5em'}}>
                              <CheckMark />
                            </div>
                          </div>
                    }
                    {
                      /* >>> call-dibs feature (revertible) >>>
                         Placed right after the green help button (traffic-light
                         yellow next to green). Always rendered — disabled while
                         the student is being helped — so the row never reflows. */
                      FEATURE_CALL_DIBS && (
                        queueEntry.gettinghelp
                          ? <div title="call dibs (unavailable while helping)" aria-label="call dibs unavailable while helping" className="col-12 col-lg px-3 my-1">
                              <div
                                className="text-center btn-disabled text-white"
                                style={{lineHeight: '2.5em'}}>
                                <Clock />
                              </div>
                            </div>
                          : <div
                              title={queueEntry.claimed ? 'release dibs' : 'call dibs'}
                              aria-label={queueEntry.claimed ? 'release dibs' : 'call dibs'}
                              className="col-12 col-lg px-3 my-1"
                              onClick={() => dispatch(claimUser(queueName, queueEntry.providerId, !queueEntry.claimed))}>
                              <div
                                className={`text-center clickable text-white ${queueEntry.claimed ? 'gray' : 'yellow'}`}
                                style={{lineHeight: '2.5em'}}>
                                <Clock />
                              </div>
                            </div>
                      )
                      /* <<< call-dibs feature <<< */
                    }
                    <div title="bad location" aria-label="bad location" className="col-12 col-lg px-3 my-1" onClick={() => dispatch(openSendBadLocationModal(queueName, queueEntry.providerId, queueEntry.badlocation))}>
                      <div
                        className="text-center purple clickable text-white"
                        style={{lineHeight: '2.5em'}}>
                        <QuestionMark />
                      </div>
                    </div>
                    <div title="copy to clipboard" aria-label="copy to clipboard" className="col-12 col-lg px-3 my-1" onClick={() => navigator.clipboard.writeText(`${queueEntry.username}\t${queueEntry.realname}\t${queueEntry.comment}`).then(() => setCopied(true)).then(() => setTimeout(() => setCopied(false), 1500))}>
                      <div
                        className="text-center gray clickable text-white"
                        style={{lineHeight: '2.5em'}}>
                        {copied ? 'Copied!' : <Copy />}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </>
      }
    </>
  );
};
